import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const PAGE_SIZES = [10, 25, 50];
const NOTIF_TYPES = ['General Notification', 'Security Alert', 'System Update', 'Promotional'];

export default function UsersPage() {
  const [users, setUsers]           = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]         = useState('');
  const searchTimer                 = useRef(null);

  const [selected, setSelected]     = useState([]); // array of email strings

  // Notification modal
  const [modal, setModal]           = useState(false);
  const [notif, setNotif]           = useState({ type: 'General Notification', title: '', message: '' });
  const [sending, setSending]       = useState(false);
  const [notifError, setNotifError] = useState('');
  const [notifSuccess, setNotifSuccess] = useState('');

  const fetchUsers = useCallback(async (page = 1, limit = 10, q = '') => {
    try {
      const { data } = await api.get('/admin/users', { params: { page, limit, search: q } });
      setUsers(data.data);
      setPagination(data.pagination);
    } catch {}
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      fetchUsers(1, pagination.limit, val);
    }, 400);
  };

  const goToPage    = (p) => fetchUsers(p, pagination.limit, search);
  const changeLimit = (l) => { fetchUsers(1, l, search); };

  // Selection helpers
  const allPageEmails  = users.map((u) => u.email);
  const allSelected    = allPageEmails.length > 0 && allPageEmails.every((e) => selected.includes(e));
  const someSelected   = allPageEmails.some((e) => selected.includes(e));

  const toggleAll = () => {
    if (allSelected) setSelected((prev) => prev.filter((e) => !allPageEmails.includes(e)));
    else             setSelected((prev) => [...new Set([...prev, ...allPageEmails])]);
  };

  const toggleOne = (email) => {
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const openModal = () => {
    setNotif({ type: 'General Notification', title: '', message: '' });
    setNotifError('');
    setNotifSuccess('');
    setModal(true);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setNotifError('');
    setNotifSuccess('');
    setSending(true);
    try {
      const { data } = await api.post('/admin/users/notify', {
        emails: selected,
        type: notif.type,
        title: notif.title,
        message: notif.message,
      });
      setNotifSuccess(data.message);
      setTimeout(() => { setModal(false); setSelected([]); }, 1800);
    } catch (err) {
      setNotifError(err.response?.data?.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const { page, totalPages, total, limit } = pagination;
  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow   = Math.min(page * limit, total);
  const pageNums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pageNums.push(i);

  return (
    <div>
      <div className="table-card">
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="card-title">👥 Users</span>
            <span style={{ fontSize: 13, color: '#718096' }}>{total} total</span>
            {selected.length > 0 && (
              <span style={us.selectedBadge}>{selected.length} selected</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {selected.length > 0 && (
              <button style={us.notifBtn} onClick={openModal}>
                🔔 Send Notification ({selected.length})
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={us.label}>Show</span>
              <select style={{ ...us.input, padding: '7px 10px', cursor: 'pointer' }}
                value={limit} onChange={(e) => changeLimit(Number(e.target.value))}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ position: 'relative' }}>
              <span style={us.searchIcon}>🔍</span>
              <input
                style={{ ...us.input, paddingLeft: 30, width: 220 }}
                placeholder="Search name or email..."
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    onChange={toggleAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Plan Expiry</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#a0aec0', padding: 32 }}>
                  {searchInput ? 'No users match your search' : 'No users found'}
                </td></tr>
              )}
              {users.map((u, i) => (
                <tr key={u.id} style={{ background: selected.includes(u.email) ? '#eff6ff' : '' }}>
                  <td>
                    <input type="checkbox" checked={selected.includes(u.email)}
                      onChange={() => toggleOne(u.email)} style={{ cursor: 'pointer' }} />
                  </td>
                  <td>{startRow + i}</td>
                  <td>{u.name || <span style={{ color: '#a0aec0' }}>—</span>}</td>
                  <td>{u.email}</td>
                  <td>{u.phone_number || <span style={{ color: '#a0aec0' }}>—</span>}</td>
                  <td style={{ fontSize: 13, color: '#718096' }}>
                    {u.plan_expiry_date ? new Date(u.plan_expiry_date).toLocaleDateString() : <span style={{ color: '#a0aec0' }}>—</span>}
                  </td>
                  <td>
                    <span className={`status-badge ${u.is_verified ? 'active' : 'expired'}`}>
                      {u.is_verified ? 'Verified' : 'Unverified'}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: '#718096' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button style={us.singleNotifBtn} onClick={() => { setSelected([u.email]); openModal(); }}>
                      🔔 Notify
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div style={us.paginationWrap}>
            <span style={us.pageInfo}>Showing {startRow}–{endRow} of {total}</span>
            <div style={us.pageButtons}>
              <button style={us.pageBtn} disabled={page === 1} onClick={() => goToPage(1)}>«</button>
              <button style={us.pageBtn} disabled={page === 1} onClick={() => goToPage(page - 1)}>‹</button>
              {pageNums.map((n) => (
                <button key={n} style={{ ...us.pageBtn, ...(n === page ? us.pageBtnActive : {}) }}
                  onClick={() => goToPage(n)}>{n}</button>
              ))}
              <button style={us.pageBtn} disabled={page === totalPages} onClick={() => goToPage(page + 1)}>›</button>
              <button style={us.pageBtn} disabled={page === totalPages} onClick={() => goToPage(totalPages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Notification Modal */}
      {modal && (
        <div style={us.backdrop} onClick={() => setModal(false)}>
          <div style={us.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={us.modalHeader}>
              <span style={us.modalTitle}>🔔 Send Notification</span>
              <button style={us.closeBtn} onClick={() => setModal(false)}>✕</button>
            </div>
            <div style={us.modalBody}>
              <form onSubmit={handleSend}>

                {/* Recipients */}
                <div style={us.fieldRow}>
                  <label style={us.label}>Recipients</label>
                  <div style={us.recipientsBox}>
                    {selected.map((email) => (
                      <span key={email} style={us.recipientTag}>
                        {email}
                        <button type="button" style={us.tagRemove}
                          onClick={() => setSelected((p) => p.filter((e) => e !== email))}>×</button>
                      </span>
                    ))}
                  </div>
                  {selected.length === 0 && (
                    <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>No recipients selected</p>
                  )}
                </div>

                {/* Notification Type */}
                <div style={us.fieldRow}>
                  <label style={us.label}>Notification Type</label>
                  <select style={{ ...us.input, width: '100%' }}
                    value={notif.type}
                    onChange={(e) => setNotif({ ...notif, type: e.target.value })}>
                    {NOTIF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Title */}
                <div style={us.fieldRow}>
                  <label style={us.label}>Title</label>
                  <input style={{ ...us.input, width: '100%' }}
                    placeholder="Security Update"
                    value={notif.title}
                    onChange={(e) => setNotif({ ...notif, title: e.target.value })}
                    required />
                </div>

                {/* Message */}
                <div style={us.fieldRow}>
                  <label style={us.label}>Message</label>
                  <textarea
                    style={{ ...us.input, width: '100%', minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
                    placeholder="Your account security settings have been updated."
                    value={notif.message}
                    onChange={(e) => setNotif({ ...notif, message: e.target.value })}
                    required
                  />
                </div>

                {notifError   && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{notifError}</p>}
                {notifSuccess && <p style={{ color: '#16a34a', fontSize: 13, marginBottom: 10 }}>{notifSuccess}</p>}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button type="button" style={us.cancelBtn} onClick={() => setModal(false)}>Cancel</button>
                  <button type="submit" style={us.sendBtn}
                    disabled={sending || selected.length === 0}>
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const us = {
  label:         { fontSize: 13, color: '#4a5568', marginBottom: 5, fontWeight: 500, display: 'block' },
  input:         { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, outline: 'none' },
  searchIcon:    { position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none' },
  selectedBadge: { background: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600 },
  notifBtn:      { padding: '8px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  singleNotifBtn:{ padding: '5px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12 },

  paginationWrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid #e2e8f0' },
  pageInfo:       { fontSize: 13, color: '#718096' },
  pageButtons:    { display: 'flex', gap: 4 },
  pageBtn:        { minWidth: 34, height: 34, padding: '0 10px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#4a5568' },
  pageBtnActive:  { background: '#2563eb', color: '#fff', borderColor: '#2563eb', fontWeight: 600 },

  backdrop:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalBox:      { background: '#fff', borderRadius: 10, width: '100%', maxWidth: 500, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
  modalTitle:    { fontSize: 16, fontWeight: 600, color: '#2d3748' },
  closeBtn:      { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#718096', lineHeight: 1 },
  modalBody:     { padding: '20px' },
  fieldRow:      { marginBottom: 16 },
  recipientsBox: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, minHeight: 42, background: '#f8fafc' },
  recipientTag:  { display: 'flex', alignItems: 'center', gap: 4, background: '#dbeafe', color: '#1d4ed8', padding: '3px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500 },
  tagRemove:     { background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 },
  cancelBtn:     { padding: '8px 20px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  sendBtn:       { padding: '8px 28px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 },
};
